import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowRight, ArrowUpRight, Check, Globe2, Quote } from "lucide-react";
import SEO from "@/components/SEO";
import BookingButton from "@/components/BookingButton";
import CTASection from "@/components/CTASection";
import ContactInfoCard from "@/components/ContactInfoCard";
import {
  COMPANY, CAPABILITIES, SERVICES, INDUSTRIES, TECHNOLOGIES,
  WHY_CHOOSE, REGIONS, CASE_STUDIES, HERO_IMAGES,
  ENTERPRISE_INDUSTRIES, EXPERTISE_AREAS, DELIVERY_MODEL,
} from "@/data/content";

const Icon = ({ name, className }) => {
  const C = Icons[name] || Icons.Circle;
  return <C className={className} />;
};

export default function Home() {
  const featuredServices = SERVICES.slice(0, 8);
  const featuredCases = CASE_STUDIES.slice(0, 3);

  return (
    <>
      <SEO
        title="Enterprise Database Consulting"
        description="DataMeridian Consulting — enterprise database consulting, SQL Server and PostgreSQL, Azure SQL Managed Instance, database performance tuning, cloud database migration and managed DBA services."
      />

      {/* HERO */}
      <section data-testid="hero" className="relative bg-[var(--dm-navy)] text-white overflow-hidden dm-grain">
        <img src={HERO_IMAGES.main} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-r from-[var(--dm-navy)] via-[var(--dm-navy)]/85 to-transparent" />
        <div className="dm-container relative py-28 lg:py-40 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8 dm-fade-up">
            <div className="dm-overline">Enterprise Database Consulting</div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02] mt-6 tracking-tight">
              The trusted meridian for <br className="hidden sm:block" /> <em className="text-[var(--dm-emerald)] not-italic">mission-critical</em> enterprise data.
            </h1>
            <div className="mt-6 inline-flex items-center gap-3">
              <span className="dm-hairline" aria-hidden="true" />
              <span className="text-sm sm:text-base uppercase tracking-[0.32em] text-white/85 font-semibold">
                {COMPANY.brandLine}
              </span>
            </div>
            <p className="mt-6 text-white/75 max-w-2xl text-lg leading-relaxed">
              DataMeridian Consulting is a boutique technology consulting company helping organizations design, modernize, optimize and support mission-critical database environments across SQL Server, PostgreSQL and the Azure data platform.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <BookingButton label="Request Technical Assessment" meetingType="consultation" variant="primary" testId="hero-request-assessment" />
              <BookingButton label="Schedule Discovery Call" meetingType="discovery" variant="ghost" testId="hero-book-discovery" />
            </div>

            {/* Capabilities — factual, no fabricated stats */}
            <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-8 max-w-3xl">
              {CAPABILITIES.map((c) => (
                <div key={c.label} className="border-t border-white/20 pt-4">
                  <div className="font-serif text-2xl leading-tight">{c.label}</div>
                  <div className="text-xs uppercase tracking-[0.2em] text-white/60 mt-2">{c.detail}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-4 hidden lg:flex flex-col justify-end">
            <div className="border border-white/15 p-6 backdrop-blur-sm bg-white/[0.04]">
              <div className="dm-overline text-white/60">Tagline</div>
              <div className="font-serif text-2xl mt-3 leading-snug">{COMPANY.tagline}</div>
              <div className="mt-6 border-t border-white/15 pt-6 text-sm text-white/60">
                Remote Consulting Worldwide — Americas · EMEA · APAC.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TECH MARQUEE — technologies only (no client/customer logos) */}
      <section data-testid="tech-marquee" className="border-b border-[var(--dm-border)] bg-white py-10 overflow-hidden">
        <div className="dm-container mb-6"><div className="dm-overline">Technology stack</div></div>
        <div className="relative w-full overflow-hidden">
          <div className="flex gap-14 dm-marquee whitespace-nowrap w-max">
            {[...TECHNOLOGIES, ...TECHNOLOGIES].map((t, i) => (
              <div key={i} className="text-[var(--dm-muted)] font-mono text-sm tracking-wider hover:text-[var(--dm-navy)] transition-colors">
                {t.name.toUpperCase()}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ENTERPRISE EXPERIENCE */}
      <section data-testid="enterprise-experience" className="bg-white">
        <div className="dm-container dm-section grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <div className="dm-overline">Enterprise Experience</div>
            <h2 className="font-serif text-4xl sm:text-5xl mt-4 text-[var(--dm-navy)] leading-tight">
              Consulting expertise developed across enterprise database environments.
            </h2>
            <p className="text-[var(--dm-muted)] mt-6 leading-relaxed">
              Our consulting expertise has been developed through enterprise database engineering initiatives supporting multinational organizations across:
            </p>
            <ul className="mt-6 grid grid-cols-2 gap-2">
              {ENTERPRISE_INDUSTRIES.map((i) => (
                <li key={i} className="flex gap-2 text-sm text-[var(--dm-navy)]">
                  <Check className="w-4 h-4 mt-1 text-[var(--dm-emerald)] shrink-0" /> {i}
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-7">
            <div className="dm-overline">Areas of Expertise</div>
            <div className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-3">
              {EXPERTISE_AREAS.map((a) => (
                <div key={a} className="flex gap-2 text-sm text-[var(--dm-navy)] border-b border-[var(--dm-border)] pb-2">
                  <span className="text-[var(--dm-emerald)] font-mono text-xs mt-1">·</span> {a}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section data-testid="services-preview" className="bg-[var(--dm-surface)]">
        <div className="dm-container dm-section">
          <div className="grid lg:grid-cols-12 gap-10 mb-14">
            <div className="lg:col-span-6">
              <div className="dm-overline">Services</div>
              <h2 className="font-serif text-4xl sm:text-5xl mt-4 text-[var(--dm-navy)] leading-tight">A full spectrum of enterprise database and data services.</h2>
            </div>
            <div className="lg:col-span-6 flex lg:justify-end lg:items-end">
              <Link to="/services" data-testid="home-services-all" className="dm-link">Explore all services <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--dm-border)]">
            {featuredServices.map((s) => (
              <Link
                key={s.slug} to={`/services/${s.slug}`}
                data-testid={`home-service-${s.slug}`}
                className="group bg-white p-8 hover:bg-[var(--dm-surface-2)] transition-colors flex flex-col min-h-[240px]"
              >
                <div className="w-10 h-10 grid place-items-center bg-[var(--dm-navy)] text-white mb-6">
                  <Icon name={s.icon} className="w-5 h-5" />
                </div>
                <div className="font-serif text-xl text-[var(--dm-navy)] leading-snug">{s.name}</div>
                <div className="text-sm text-[var(--dm-muted)] mt-3 leading-relaxed">{s.tagline}</div>
                <div className="mt-auto pt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-[var(--dm-emerald)]">
                  Explore <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section data-testid="industries-preview" className="bg-white">
        <div className="dm-container dm-section">
          <div className="grid lg:grid-cols-12 gap-10 mb-14">
            <div className="lg:col-span-6">
              <div className="dm-overline">Industries</div>
              <h2 className="font-serif text-4xl sm:text-5xl mt-4 text-[var(--dm-navy)] leading-tight">Consulting focused on the industries that depend on data.</h2>
            </div>
            <div className="lg:col-span-6 flex lg:justify-end lg:items-end">
              <p className="text-[var(--dm-muted)] max-w-md">From manufacturing and logistics to financial services and SaaS, our engagements are shaped by the operational reality of the industry we are serving.</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--dm-border)]">
            {INDUSTRIES.map((i) => (
              <Link key={i.slug} to={`/industries/${i.slug}`} data-testid={`home-industry-${i.slug}`} className="group bg-white p-6 hover:bg-[var(--dm-navy)] hover:text-white transition-colors">
                <Icon name={i.icon} className="w-6 h-6 text-[var(--dm-emerald)] group-hover:text-white transition-colors" />
                <div className="font-serif text-lg mt-4">{i.name}</div>
                <div className="text-xs text-[var(--dm-muted)] group-hover:text-white/70 mt-2 leading-relaxed">{i.summary}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US — meaningful strengths, no fake numbers */}
      <section data-testid="why-us" className="bg-[var(--dm-surface)]">
        <div className="dm-container dm-section">
          <div className="grid lg:grid-cols-12 gap-16">
            <div className="lg:col-span-4">
              <div className="dm-overline">Why DataMeridian</div>
              <h2 className="font-serif text-4xl sm:text-5xl mt-4 text-[var(--dm-navy)] leading-tight">Consulting that behaves like an extension of your team.</h2>
              <p className="text-[var(--dm-muted)] mt-6 leading-relaxed">A boutique practice by design — senior specialists, engineered engagements, and outcomes measured in the language of the business.</p>
            </div>
            <div className="lg:col-span-8 grid sm:grid-cols-2 gap-x-10 gap-y-8">
              {WHY_CHOOSE.map((w) => (
                <div key={w.title} className="border-t border-[var(--dm-border)] pt-5">
                  <Check className="w-5 h-5 text-[var(--dm-emerald)]" />
                  <div className="font-serif text-lg text-[var(--dm-navy)] mt-3">{w.title}</div>
                  <div className="text-sm text-[var(--dm-muted)] mt-2 leading-relaxed">{w.body}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* DELIVERY MODEL */}
      <section data-testid="delivery-model" className="bg-white">
        <div className="dm-container dm-section">
          <div className="grid lg:grid-cols-12 gap-10 mb-14">
            <div className="lg:col-span-6">
              <div className="dm-overline">Our Delivery Model</div>
              <h2 className="font-serif text-4xl sm:text-5xl mt-4 text-[var(--dm-navy)] leading-tight">A structured path from discovery to continuous support.</h2>
            </div>
            <div className="lg:col-span-6 flex lg:justify-end lg:items-end">
              <p className="text-[var(--dm-muted)] max-w-md">Every engagement follows a repeatable delivery model — visible, measurable and easy to plan around.</p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--dm-border)]">
            {DELIVERY_MODEL.map((p) => (
              <div key={p.step} className="bg-white p-8">
                <div className="font-mono text-xs text-[var(--dm-emerald)]">{p.step}</div>
                <div className="font-serif text-xl text-[var(--dm-navy)] mt-3">{p.title}</div>
                <div className="text-sm text-[var(--dm-muted)] mt-2 leading-relaxed">{p.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CASE STUDIES */}
      <section data-testid="case-studies-preview" className="bg-[var(--dm-navy)] text-white">
        <div className="dm-container dm-section">
          <div className="grid lg:grid-cols-12 gap-10 mb-14">
            <div className="lg:col-span-6">
              <div className="dm-overline text-white/60">Engagement examples</div>
              <h2 className="font-serif text-4xl sm:text-5xl mt-4 leading-tight">Representative outcomes from enterprise engagements.</h2>
            </div>
            <div className="lg:col-span-6 flex lg:justify-end lg:items-end">
              <Link to="/case-studies" className="text-[var(--dm-emerald)] font-semibold inline-flex items-center gap-2 hover:underline">View examples <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {featuredCases.map((c) => (
              <Link key={c.slug} to={`/case-studies/${c.slug}`} data-testid={`home-case-${c.slug}`} className="group bg-white/[0.03] border border-white/10 hover:border-[var(--dm-emerald)] transition-colors">
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={c.image} alt={c.title} className="w-full h-full object-cover opacity-70 group-hover:opacity-100 transition-opacity" />
                </div>
                <div className="p-6">
                  <div className="dm-overline text-white/50">{c.industry}</div>
                  <div className="font-serif text-xl mt-3 leading-snug">{c.title}</div>
                  <div className="mt-4 text-sm text-white/70">{c.summary}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* GLOBAL DELIVERY */}
      <section data-testid="global-delivery" className="bg-white">
        <div className="dm-container dm-section grid lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <div className="dm-overline">Global remote delivery</div>
            <h2 className="font-serif text-4xl sm:text-5xl mt-4 text-[var(--dm-navy)] leading-tight">Remote consulting worldwide.</h2>
            <p className="text-[var(--dm-muted)] mt-6 leading-relaxed max-w-lg">A single accountable delivery partner for enterprise database and data engineering programmes — engaged remotely across the following regions.</p>
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-2 max-w-xl">
              {REGIONS.map((r) => (
                <div key={r} className="flex items-center gap-2 text-sm text-[var(--dm-navy)]">
                  <Globe2 className="w-3.5 h-3.5 text-[var(--dm-emerald)]" /> {r}
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="aspect-[4/3] overflow-hidden border border-[var(--dm-border)]">
              <img src={HERO_IMAGES.global} alt="Global remote delivery" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* CLIENT SUCCESS — no fake testimonials; confidentiality-first */}
      <section data-testid="client-success" className="bg-[var(--dm-surface)]">
        <div className="dm-container dm-section">
          <div className="dm-overline">Client Success Stories</div>
          <h2 className="font-serif text-4xl sm:text-5xl mt-4 text-[var(--dm-navy)] leading-tight max-w-3xl">Published only with client approval.</h2>
          <div className="mt-10 border border-[var(--dm-border)] bg-white p-10 lg:p-12 max-w-4xl">
            <Quote className="w-8 h-8 text-[var(--dm-emerald)]" />
            <p className="font-serif text-2xl leading-snug text-[var(--dm-navy)] mt-5">
              To respect client confidentiality and non-disclosure obligations, customer success stories are published only with client approval.
            </p>
            <p className="text-sm text-[var(--dm-muted)] mt-6 leading-relaxed max-w-2xl">
              Detailed engagement references are available on request during our proposal process. Every reference is shared with the explicit consent of the organization involved.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Information — before CTA */}
      <section data-testid="home-contact-info" className="bg-white">
        <div className="dm-container dm-section grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-6">
            <div className="dm-overline">Get in touch</div>
            <h2 className="font-serif text-4xl sm:text-5xl mt-4 text-[var(--dm-navy)] leading-tight">Speak with a senior consultant, not a sales team.</h2>
            <p className="text-[var(--dm-muted)] mt-6 leading-relaxed max-w-lg">Every enquiry is read by a senior specialist. We respond within one business day and never pass leads to a call centre.</p>
          </div>
          <div className="lg:col-span-6">
            <ContactInfoCard variant="light" testId="home-contact-info-card" />
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
